import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Award, Users, Building2, Target, TrendingUp, Globe } from "lucide-react";

export default function AboutPage() {
  const milestones = [
    { year: "1998", title: "Foundation", description: "HOKICO established with vision to revolutionize retail displays" },
    { year: "2005", title: "Expansion", description: "Opened state-of-the-art 42,000 sq ft manufacturing facility" },
    { year: "2010", title: "Innovation", description: "Launched foldable and eco-friendly display solutions" },
    { year: "2015", title: "Pan-India", description: "Expanded operations to 115+ cities across India" },
    { year: "2020", title: "Digital Transformation", description: "Integrated German automated machinery and digital processes" },
    { year: "2026", title: "Industry Leader", description: "Serving 90+ enterprise clients with 1,350+ completed projects" },
  ];

  const values = [
    { icon: Target, title: "Innovation", description: "Constantly evolving with market trends and client needs" },
    { icon: Award, title: "Quality", description: "ISO-certified processes ensuring international standards" },
    { icon: Users, title: "Partnership", description: "Long-term relationships built on trust and delivery" },
    { icon: TrendingUp, title: "Excellence", description: "26+ years of consistent growth and client satisfaction" },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 border-b">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope' }} data-testid="about-heading">
              Our Story
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              For over 26 years, HOKICO has been at the forefront of retail display manufacturing, transforming how brands connect with consumers through innovative Point of Sale Material (POSM) solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="p-8 border border-border/50 rounded-sm bg-white">
              <Building2 className="h-12 w-12 text-primary mb-4" />
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Our Mission</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                To empower brands with premium, innovative, and sustainable retail display solutions that enhance visibility, drive sales, and create memorable customer experiences.
              </p>
            </div>
            
            <div className="p-8 border border-border/50 rounded-sm bg-white">
              <Globe className="h-12 w-12 text-secondary mb-4" />
              <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Our Vision</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                To be India's most trusted partner for enterprise-grade retail displays, recognized for quality, innovation, and sustainable manufacturing practices that set industry benchmarks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-muted/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>Our Core Values</h2>
            <p className="text-lg text-muted-foreground">The principles that drive everything we do</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="p-6 bg-white border border-border/50 rounded-sm text-center">
                  <IconComponent className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope' }}>Our Journey</h2>
            <p className="text-lg text-muted-foreground">26+ years of innovation and growth</p>
          </div>

          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex gap-6 items-start">
                <div className="flex-shrink-0 w-24 text-right">
                  <div className="text-2xl font-bold text-primary" style={{ fontFamily: 'Manrope' }}>{milestone.year}</div>
                </div>
                <div className="flex-shrink-0 w-4 pt-2">
                  <div className="w-4 h-4 rounded-full bg-primary" />
                  {index < milestones.length - 1 && (
                    <div className="w-0.5 h-20 bg-primary/30 ml-1.5 mt-2" />
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>{milestone.title}</h3>
                  <p className="text-muted-foreground">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>26+</div>
              <div className="text-lg">Years of Excellence</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>1,350+</div>
              <div className="text-lg">Projects Delivered</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>115+</div>
              <div className="text-lg">Cities Covered</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>90+</div>
              <div className="text-lg">Enterprise Clients</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}